// https://pinia.vuejs.org/core-concepts/
import { defineStore } from 'pinia';
import { ref, computed, h } from 'vue';
import { getExplanation } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';

const defaultState = () => ({
  headers: {},
  // This is the portfolio that will be used for the analytics
  // If multiple are selected, they will be merged into one before running analytics.
  // These are the selected portfolios
  portfolios: [],
  // These are the holdings for all the selected portfolios
  // { modelportfolio_abc123: [...] }
  holdingsByPortfolio: {}, // holdings from shareownerships and 'unmatched'
  uploadedDFByPortfolio: {}, // holdings from uploaded_df
  portfolio_checkup_id: null,
  portfolio_checkup_created_date: null,
  useWholeShares: false,
  useWholeSharesExplicit: null, // null = not set by user; true/false = explicit user choice
  charts: {},
  slideImages: {},
  includeSlides: {},
  areas: [],
  loadingStates: {
    isPortfolioLoading: false,
    isBenchmarkLoading: false,
    isPreferencesLoading: false,
  },
  manualClientRisk: null,
  riskBasedBenchmark: null,
  showClientRiskScore: true,
  selectedValues: [],
  methodologyData: {},
  featureFlags: [],
  use_northfield: false,
  useMock: false,
  personEntity: null,
  specificInclusions: [],
  specificExclusions: [],
  specificDoNotBuy: [],
  specificDoNotSell: [],
  availableSymbols: [],
  accountValue: null,
  accounts: [],
  overrides: [],
  symbolProxies: [],
  unresolvedTaxLotSymbols: [],
  fees: {
    schedule: null, // proposed portfolio advisory fee schedule
    currentPortfolioSchedule: null, // current/starting portfolio advisory fee schedule
    benchmarkSchedule: null, // benchmark portfolio advisory fee schedule
    portfolio: {
      advisoryFee: 0,
      managementFee: 0,
    },
    benchmark: {
      advisoryFee: 0,
      managementFee: 0,
    },
    optimized: {
      advisoryFee: 0,
      managementFee: 0,
    },
  },
  completeValues: [],
  orionTaxLots: [], // Array of { type: 'orion' | 'model', portfolioId: string, name: string, exclusions: string[], entityId: string|null }
  maxLTCG: null,
  minLTCG: null,
  maxSTCG: null,
  minSTCG: null,
  totalTax: null,
  shortTermRate: .38,
  longTermRate: .20,
  harvestAllTaxLosses: false,
  harvestOnlyTaxLosses: false,
  optimizeAssetLocation: false,
  perAccountMaxHoldings: false,
  cashTarget: 0,
  tryToSellExclusions: true,
  onlySell: false,
  onlyBuy: false,
  ignoredSecurities: [],
  constrain_us_stock_exposure: true, // Default to true to maintain current behavior
  equityFocus: true, // Default to true to maintain current equity constraint behavior
  files: [],
  csvExports: [],
  activeNotifications: {},
  optimizationError: null,
  taskCheckInterval: null,
  optimization: {},
  activeArea: 'Start',
  optimizerChecked: false,
  combineOrionAccounts: false,
  // Optional benchmark used by the optimizer instead of the default risk-based ACWI/AGG
  // VADIS benchmark. Either a model-portfolio entity or an ETF/mutual-fund ticker.
  // Shape: { entityId, tickerSymbol, name } — exactly one of entityId / tickerSymbol set.
  customBenchmark: null,
  isOptimizationLoading: false,
  taxTransitionPlan: {},
  stressTestResults: [],
  stressTestChartData: null,
  optimizationAdditionalWarnings: {},
  lastOptimizerInputs: {},
  chartImageUpdateRequested: null,
  lastImageUpdates: {},
  zipExportRequested: false,
  optimizerSettingsDataUrl: null,
});

// It is convention to prefix store names here with "use"
export const useCheckupStore = defineStore('checkup', {
  state: () => (defaultState()),

  getters: {
    isCheckedPortfolios() {
      return this.portfolios.filter(portfolio => portfolio.isChecked);
    },
    selectedValueIds() {
      return this.selectedValues.map(value => value.id);
    },
    benchmark() {
      return this.portfolios.filter(portfolio => portfolio.label === 'benchmark')[0];
    },
    portfolio() {
      return this.portfolios.find(portfolio => portfolio.label === 'combinedPortfolio');
    },
    optimizedPortfolio() {
      return this.portfolios.filter(portfolio => portfolio.label === 'optimizedPortfolio')[0];
    },
    selectedPortfolios() {
      return this.portfolios.filter(portfolio => portfolio.label === 'portfolio');
    },
    overridesJSON() {
      return this.overrides.map(override => {
        return { min: override.minWeight/100, max: override.maxWeight/100, symbol: override.symbol };
      }).filter(override => {
        return (Number.isFinite(override.min) && Number.isFinite(override.max));
      });
    },
    currentHoldingsByTaxStatus() {
      const optimizations = this.optimization?.optimizations || [];
      const totals = {};
      for (const opt of optimizations) {
        const status = opt.current_holdings_by_tax_status || {};
        for (const [taxStatus, dollars] of Object.entries(status)) {
          totals[taxStatus] = (totals[taxStatus] || 0) + dollars;
        }
      }
      return totals;
    },
    areasByName() {
      const byName = {};
      if (!this.areas?.length) return {};

      this.areas.forEach(area => {
        if (area.name) byName[area.name] = area;
      });
      return byName;
    },
    clientRisk() {
      return this.manualClientRisk || { value: this.personEntity?.risk_score };
    },
    totalsByAccount() {
      const byAccount = { };

      // We need to add in the unmatched holdings and then loop through everything
      // Also add to overall and account totals

      this.portfolio?.holdings?.forEach(holding => {
        const account = holding.account;
        const dollars = holding.dollars;
        const costbasis = holding.costbasis;

        const current = byAccount[account] || 0;
        byAccount[account] = current ? current + dollars : dollars;
      });

      this.portfolio?.unmatched_holdings?.forEach(holding => {
        const account = holding.account;
        const dollars = holding.dollars;
        const costbasis = holding.costbasis;

        const current = byAccount[account] || 0;
        byAccount[account] = current ? current + dollars : dollars;
      });

      // Get total of all accounts and add to object
      const total = Object.values(byAccount).reduce((a, b) => a + b, 0);
      return { total, accountTotals: Object.keys(byAccount).map(key => ({ name: key, total: byAccount[key] })) };
    },
    isLoading() {
      return Object.values(this.loadingStates).some(state => state);
    },
    matchedHoldingsCount() {
      return this.portfolio?.holdings?.length || 0;
    },
    unmatchedHoldingsCount() {
      return this.portfolio?.unmatched_holdings?.length || 0;
    },
    portfolioHoldingsCount() {
      return this.matchedHoldingsCount + this.unmatchedHoldingsCount;
    },
    noMatchedHoldings() {
      return this.matchedHoldingsCount === 0 && this.unmatchedHoldingsCount > 0;
    },
    filteredAreas() {
      return this.areas.filter(area => {
        if (area.name === 'Trailing Returns') return false;
        if (area.name === 'Values Alignment' && !this.selectedValueIds?.length) return false;
        return this.includeSlides[area.name];
      });
    },
    /*
      This is an array of the holdings from all selected portfolios
      Note: Due to the evolution of this feature, we have both this and checkupStore.portfolio.holdings.
      This is probably confusing. A brief attempt to explain the difference:
        One or more selected portfolios > Fetch holdings for each > holdingsByPortfolio > holdingsFromPortfolios > Holdings tab with editing
        One or more selected portfolios > Run Analytics > Portfolios merged together > checkupStore.portfolio > Analytics
    */
    holdingsFromPortfolios() {
      const merged = [];
      Object.entries(this.holdingsByPortfolio).forEach(([portfolioID, holdings]) => {
        console.log('checkupStore.holdingsFromPortfolios: Portfolio ID', portfolioID);
        const withID = holdings?.map(holding => ({
          ...holding,
          portfolioID: portfolioID,
        })) || [];
        merged.push(...withID);
      });
      return merged;
    },
    uploadPendingFiles() {
      return this.files.filter(f => f.uploadStatus === 'uploadPending');
    },
    uploadInvalidFiles() {
      return this.files.filter(f => f.uploadStatus === 'uploadInvalid');
    },
    uploadFailedFiles() {
      return this.files.filter(f => f.uploadStatus === 'uploadFailed');
    },
    whichNotifications() {
      return Object.keys(this.activeNotifications);
    },
    hasNotifications() {
      return Object.keys(this.activeNotifications).length > 0;
    },
    clearNotifications() {
      this.activeNotifications = {};
    },
  },
  actions: {
    resetState() {
      Object.assign(this.$state, defaultState());
    },
    // updatePortfolioTaxabilityStatus(portfolioId, taxabilityStatus) {
    //   const portfolioIndex = this.portfolios.findIndex(p => p.id === portfolioId);
    //   if (portfolioIndex !== -1) {
    //     this.portfolios[portfolioIndex].taxabilityStatus = taxabilityStatus;
    //   }
    // },
    addPortfolio(portfolio, label) {
      this.portfolios.push({ ...portfolio, label, isChecked: true });
    },
    updatePortfolio(portfolio) {
      console.log('checkupStore.updatePortfolio: Updating portfolio', portfolio.name, portfolio.external_id, portfolio.label);
      console.log('checkupStore.updatePortfolio: this.portfolios', this.portfolios);

      this.portfolios = this.portfolios.map(p => {
        // TODO: OR condition below should be a temporary hack. There is some weirdness with id vs external_id, but we couldn't find the issue yet.
        if (p.external_id === portfolio.external_id && p.label === portfolio.label) {
          console.log('checkupStore.updatePortfolio: Found portfolio', portfolio);
          return portfolio;
        } else {
          return p;
        }
      });
    },
    removePortfolio(portfolio) {
      if (portfolio?.id == 'default_benchmark') {
        this.portfolios = this.portfolios.filter(p => p.id !== 'default_benchmark');
        console.log('checkupStore.removePortfolio: Removed default benchmark');
      } else if (!portfolio?.external_id) {
        return;
      } else {
        this.portfolios = this.portfolios.filter(p => p.external_id !== portfolio.external_id && p.label !== portfolio.label);
        console.log('checkupStore.removePortfolio: Removed portfolio', portfolio.external_id, 'with label', portfolio.label);
      }
    },
    clearPortfolioNotifications() {
      const keysToRemove = [
        'NO_MATCHED_HOLDINGS_PORTFOLIO',
        'MISSING_TICKER_SYMBOLS_PORTFOLIO',
        'MISSING_TICKER_SYMBOLS',
        'NO_MATCHED_HOLDINGS',
      ];

      keysToRemove.forEach(key => {
        if (this.activeNotifications[key]) {
          delete this.activeNotifications[key];
        }
      });
    },
    addFile(file, uploadStatus) {
      if (this.files.some(f => f.name === file.name)) {
        console.log('Adding DUPE_FILENAME notification');
        this.addNotification('DUPE_FILENAME', {
          dupedFilenames: this.files.filter(f => f.name === file.name),
        });
        return;
      }
      this.files.push({ name: file.name, fileObject: file, uploadStatus });
    },
    updateFile(file) {
      this.files = this.files.map(f => (f.name === file.name) ? file : f);
    },
    addNotification(notificationKey, notificationInfo={}) {
      this.activeNotifications = { ...this.activeNotifications, [notificationKey]: notificationInfo };
    },
    async updateHoldingsByPortfolio(external_id=null) {
      console.log('updatePortfolioHoldings', external_id || 'ALL');
      const newHoldings = {};
      const newDF = {};
      let num_unmatched = 0;
      let total_holdings = 0;

      this.clearPortfolioNotifications(); // clear portfolio notifications before looping through current portfolios
      if (Object.entries(this.activeNotifications).length === 0) {
        this.activeNotifications = {};
      }

      // track relevant notifications
      const selectedPortfolioNames = [];
      let showNotifications = false;
      const notificationInfo = {
        MISSING_TICKER_SYMBOLS_PORTFOLIO: { portfolios: [] },
        NO_MATCHED_HOLDINGS_PORTFOLIO: { portfolios: [] },
      };

      // Doing it this way ensures that we also remove holdings when a portfolio is unselected
      for (const portfolio of this.selectedPortfolios) {
        // If the id matches, this is the portfolio we want to refresh.
        // Or if null, we want to refresh all of them.
        // store the selected portfolio names (push)
        // later see if the names are in the notification info, and only keep those that are in this list

        const name = portfolio.name;
        selectedPortfolioNames.push(name);

        if (external_id === null || portfolio.external_id === external_id) {
          console.log('Updating portfolio holdings', portfolio.external_id);
          const [holdings, uploaded_df_agg] = await this.getPortfolioHoldings(portfolio.external_id);
          newHoldings[portfolio.external_id] = holdings;
          newDF[portfolio.external_id] = uploaded_df_agg;
          // track number of unmatched holdings in portfolios that are selected
          const unmatched = portfolio.unmatched_holdings?.length || 0;
          const num_holdings = holdings.length || 0;
          num_unmatched += unmatched;
          total_holdings += num_holdings;
          // check unmatched for current portfolio
          if (unmatched == num_holdings) {
            showNotifications = true;
            // only show this notification if there isn't already a success notification for this portfolio from a PDF upload
            // to avoid tons of notifications
            if (Object.keys(this.activeNotifications).length === 0 ||
              !Object.keys(this.activeNotifications).some(key =>
                key.includes('SUCCESS') &&
                  this.activeNotifications[key].statements &&
                  this.activeNotifications[key].statements.some(statementName => {
                    const cleanStatementName = statementName.replace('.pdf', '');
                    console.log('Checking portfolio name:', cleanStatementName);
                    return cleanStatementName === name.replace('.pdf', '');
                  }),

              )) {
              notificationInfo.NO_MATCHED_HOLDINGS_PORTFOLIO.portfolios.push(name);
            };
          };
        } else {
          // Otherwise just copy them over
          newHoldings[portfolio.external_id] = this.holdingsByPortfolio[portfolio.external_id];
          newDF[portfolio.external_id] = this.uploadedDFByPortfolio[portfolio.external_id];
        }
      }

      console.log('selected portfolios', selectedPortfolioNames);
      // Update the holdings in the checkupStore
      this.holdingsByPortfolio = newHoldings;
      this.uploadedDFByPortfolio = newDF;

      // Filter notificationInfo portfolios to ensure they are in the current selected portfolios
      notificationInfo.NO_MATCHED_HOLDINGS_PORTFOLIO.portfolios = notificationInfo.NO_MATCHED_HOLDINGS_PORTFOLIO.portfolios.filter(portfolioName => selectedPortfolioNames.includes(portfolioName));
      notificationInfo.MISSING_TICKER_SYMBOLS_PORTFOLIO.portfolios = notificationInfo.MISSING_TICKER_SYMBOLS_PORTFOLIO.portfolios.filter(portfolioName => selectedPortfolioNames.includes(portfolioName));

      // actually add the notifications
      if (notificationInfo.NO_MATCHED_HOLDINGS_PORTFOLIO.portfolios.length > 0) {
        this.addNotification('NO_MATCHED_HOLDINGS_PORTFOLIO', notificationInfo.NO_MATCHED_HOLDINGS_PORTFOLIO);
      }

      if (notificationInfo.MISSING_TICKER_SYMBOLS_PORTFOLIO.portfolios.length > 0) {
        this.addNotification('MISSING_TICKER_SYMBOLS_PORTFOLIO', notificationInfo.MISSING_TICKER_SYMBOLS_PORTFOLIO);
      }
      // this is a double check, probably unnecessary
      if (!showNotifications) {
        console.log('no notifications triggered by current portfolios, clearing all portfolio notifications');
        this.clearPortfolioNotifications();
        if (Object.entries(this.activeNotifications).length === 0) {
          console.log('active notifications is empty');
          this.activeNotifications = {};
        }
      };
    },
    async getPortfolioHoldings(id) {
      const url = `/api/v1/entities/${id}/holdings/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();

      // Matched holdings
      const matched = data.portfolio.holdings;

      // Unmatched holdings
      const unmatched = data.portfolio.unmatched_holdings;

      // uploaded_df contains matched and unmatched holdings in order
      const uploaded_df_agg = data.portfolio.uploaded_df_agg;

      const holdings_list = [...matched, ...unmatched];

      return [holdings_list, uploaded_df_agg];
    },
    updateAreaChartType(areaName, chartType) {
      const area = this.areas.find(area => area.name === areaName);

      area.chartData.forEach(chart => {
        if (chart.library === 'highcharts') {
          chart.series.forEach(series => {
            series.type = chartType;
          });
        } else {
          chart.type = chartType;
        }
      });

      this.updateArea(areaName, area);
      console.log('Areas after updateAreaChartType', this.areas);
    },
    setPersonEntity(personEntity) {
      console.log('SETTING Person Entity', this.personEntity);
      this.personEntity = personEntity;
    },
    updateAreasWithExplanation() {
      console.log('IN UPDATE AREAS WITH EXPLANATION');
      const portfolioName = this.portfolio?.name;
      const benchmarkName = this.benchmark?.name;
      const proposedName = this.optimizedPortfolio?.name;
      // TODO: What should we do if one of the names is missing?

      this.areas.forEach(area => {
        // Get AI explanation text for each area
        let namedarea = area;
        if (area.name === 'Returns') {
          namedarea = this.areasByName['Trailing Returns'];
        }


        const cleanedArea = this.cleanAreaForExplanation(namedarea);

        getExplanation({ ...cleanedArea, portfolioName, benchmarkName, proposedName }).then(explanation => {
          // Update each area with explanation when available (don't wait for all to finish)
          console.log('THIS AREA EXPLANATION HERE', explanation);
          this.updateOneAreaExplanation(area.name, explanation);
          console.log(`Got AI explanation for area ${area.name}`);
          //
        }).catch(e => {
          console.log('E ERROR IS HERE', e);
          // Show user friendly error message when explanation fails to load
          this.updateOneAreaExplanation(area.name, 'We had trouble generating a summary for this data. Please try again later or contact support.');
          console.error(`Failed to get AI explanation for area ${area.name}`, area);
          throw e;
        });
      });
    },
    // Modify area data as needed before sending to AI for explanation
    // First Use Case: Remove pointFormat prop from chartData to avoid being blocked by CloudFront WAF rule for XSS
    cleanAreaForExplanation(area) {
      // Create a deep copy of the area to avoid modifying the original
      const cleanedArea = JSON.parse(JSON.stringify(area));
      const namesToCheck = ['Style Category Breakdown', 'Extended Style', 'Asset Class Breakdown', 'Asset Location'];
      if (namesToCheck.includes(cleanedArea.name)) {
        const { chartData, ...rest } = cleanedArea;
        return rest;
      }
      if (cleanedArea.chartData) {
        cleanedArea.chartData.forEach(chart => {
          if (chart.library !== 'highcharts') return;
          chart.series.forEach(series => {
            if (series?.tooltip?.pointFormat) delete series.tooltip.pointFormat;
          });
        });
      }

      return cleanedArea;
    },
    updateOneAreaExplanation(areaName, explanation) {
      const newAreas = this.areas.map(area => {
        if (area.name === areaName) {
          return { ...area, explanation };
        }
        return area;
      });
      this.setAreas(newAreas);
    },
    async updateAreaExplanationWithFetch(areaName) {
      try {
        const portfolioName = this.portfolio?.name;
        const benchmarkName = this.benchmark?.name;
        const proposedName = this.optimizedPortfolio?.name;
        // Fetch the explanation for the area
        const areaData = this.areas.find(area => area.name === areaName);
        if (!areaData) {
          throw new Error('No area with name found');
        }
        const explanation = await getExplanation({ ...areaData, portfolioName, benchmarkName, proposedName });
        // Update the area with the fetched explanation
        this.updateOneAreaExplanation(areaName, explanation);
        console.log(`Updated explanation for area ${areaName}`);
      } catch (e) {
        // Handle errors, such as by logging or setting an error state
        console.error(`Failed to update explanation for area ${areaName}`, e);
        this.updateOneAreaExplanation(areaName, 'Failed to fetch explanation. Please try again later.');
      }
    },
    saveCSRF(csrf) {
      console.log('SAVING CSRF', csrf);
      this.headers = { 'X-CSRFToken': csrf };
    },
    async getSymbols() {
      if (!this.availableSymbols || this.availableSymbols.length == 0) {
        const url = '/api/v1/equity_symbols';
        const data = await fetch(url, { method: 'GET', headers: this.headers });
        const j = await data.json();
        this.availableSymbols = j.symbols;
        this.availableSymbols.sort((x, y) =>
          x.length - y.length || x.localeCompare(y),
        );
        console.log('Setting available symbols to ', j);
        console.log('Available symbols', this.availableSymbols);
      } else {
        console.log('Already have available symbols');
      }
    },
    async saveCheckup(update_checkup = false) {
      // Create FormData for the large JSON objects
      const formData = new FormData();
      if (this.portfolio) {
        const currentBlob = new Blob([JSON.stringify(this.portfolio)], { type: 'application/json' });
        formData.append('current_json_file', currentBlob, 'current.json');
      }
      if (this.benchmark) {
        const benchmarkBlob = new Blob([JSON.stringify(this.benchmark)], { type: 'application/json' });
        formData.append('benchmark_json_file', benchmarkBlob, 'benchmark.json');
      }
      if (this.optimizedPortfolio) {
        const proposedBlob = new Blob([JSON.stringify(this.optimizedPortfolio)], { type: 'application/json' });
        formData.append('proposed_json_file', proposedBlob, 'proposed.json');
      }
      // Add all the other data as JSON in a single field
      const otherData = {
        portfolio_id: this.portfolio?.external_id,
        benchmark_id: this.benchmark?.external_id,
        proposed_id: this.optimizedPortfolio?.external_id,
        person_entity_id: this.personEntity?.external_id,
        values: this.selectedValueIds.join(','),
        client_risk: this.clientRisk?.value,
        portfolio_checkup_id: this.portfolio_checkup_id,
        specific_inclusions: this.specificInclusions,
        specific_exclusions: this.specificExclusions,
        specific_donotbuy: this.specificDoNotBuy,
        specific_donotsell: this.specificDoNotSell,
        update_checkup: update_checkup,
        fees: this.fees,
      };

      formData.append('data', JSON.stringify(otherData));

      const result = await fetch('/api/v1/portfolio_checkups/', {
        method: 'POST',
        headers: this.headers, // Omit Content-Type to let browser set it
        body: formData,
      });

      const { portfolio_checkup_id, portfolio_checkup_created_date } = await result.json();
      console.log('PORTFOLIO CHECKUP ID', portfolio_checkup_id);
      this.portfolio_checkup_id = portfolio_checkup_id;
      this.portfolio_checkup_created_date = portfolio_checkup_created_date;
      return this.portfolio_checkup_id;
    },
    async getCheckup(checkupId) {
      const result = await fetch(`/api/v1/portfolio_checkups/${checkupId}/`, {
        method: 'GET',
        headers: { ...this.headers, 'Content-Type': 'application/json' },
      });
      if (!result.ok) {
        throw new Error(`Get checkup error, status: ${result.status}`);
      }
      console.error('right before get checkup data');
      const data = await result.json();
      console.error('getCheckup, data: ', data);
      this.portfolio_checkup_created_date = data.created;
      return data;
    },
    async sendToOrion() {
      const portfolioName = prompt('What do you want to name this portfolio in Orion?', this.benchmark?.name);
      if (portfolioName === null) {
        return;
      }
      const url = '/api/v1/send_to_orion/';
      const data = await fetch(url, { method: 'POST', headers: { ...this.headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ 'portfolio_checkup_id': this.portfolio_checkup_id, 'portfolio_name': portfolioName }) });
      if (data.status === 200) {
        alert('Successfully sent to Orion. It may take a few minutes for the portfolio to show up in Eclipse. It should show up under strategies/security sets in Eclipse.');
      } else {
        alert('Error sending to Astro');
      }
      console.log('sendToOrion, data: ', data);
    },
    async computeVadis(account, lotsOverride = null) {
      console.log('COMPUTING VADIS', account);
      const accountValue = account ? parseFloat(account?.dollars?.replace(/[^0-9\.]/g, '')) : null;
      // When we have an explicit account object, its custodian-based flag is authoritative.
      // For pure Orion-lots mode (no account), infer from the Orion lot custodians.
      // In both cases the user's explicit checkbox choice always wins.
      const activeLots = lotsOverride || this.orionTaxLots;
      const orionImpliesWholeShares = activeLots.some(t => t.wholeShares !== false);
      const autoWholeShares = account ?
        !!(account?.useWholeShares) :
        orionImpliesWholeShares;
      const useWholeShares = this.useWholeSharesExplicit !== null ?
        this.useWholeSharesExplicit :
        autoWholeShares;
      const url = '/api/v1/start_portfolio_checkup_optimization/';

      const baseConfig = {
        'portfolio_checkup_id': this.portfolio_checkup_id,
        'use_northfield': this.use_northfield,
        'use_mock': this.useMock,
        'account_value': accountValue,
        'use_whole_shares': useWholeShares,
        'account': account,
        // In separate mode (multiple Orion accounts, not combined), per-account cash controls
        // are hidden in the UI. Strip any stale custom values and send defaults so they match
        // the household-level CASH_USD constraint from the optimizer.
        'orion_tax_lots': activeLots.length ? (() => {
          const totalOrionAccounts = this.orionTaxLots.filter(t => t.type === 'orion').length;
          // Per-account cash controls are hidden (disabled) unless multiple accounts are combined.
          // Strip stale values in that case so defaults reach the backend.
          const perAccountCashActive = totalOrionAccounts > 1 && this.combineOrionAccounts;
          const separateMode = !perAccountCashActive;
          return activeLots.map(t => ({
            type: t.type,
            name: t.name,
            account_id: t.accountId || null,
            portfolio_id: t.portfolioId || null,
            entity_id: t.entityId || null,
            exclusions: t.exclusions || [],
            taxable: t.taxable !== false,
            account_type_name: t.accountTypeName || null,
            whole_shares: t.wholeShares !== false,
            inflow: t.inflow || 0,
            cash_min: separateMode ? 0.009 : (t.cashMin != null ? t.cashMin : 0.009),
            cash_max: separateMode ? 0.01 : (t.cashMax != null ? t.cashMax : 0.01),
          }));
        })() : null,
        'min_stcg': this.minSTCG,
        'max_stcg': this.maxSTCG,
        'min_ltcg': this.minLTCG,
        'max_ltcg': this.maxLTCG,
        'total_tax': this.totalTax,
        'short_term_rate': this.shortTermRate,
        'long_term_rate': this.longTermRate,
        'harvest_all_tax_losses': this.harvestAllTaxLosses,
        'harvest_only_tax_losses': this.harvestOnlyTaxLosses,
        'optimize_asset_location': this.optimizeAssetLocation,
        'per_account_max_holdings': this.perAccountMaxHoldings,
        'cash_target': this.cashTarget,
        'try_to_sell_exclusions': this.tryToSellExclusions,
        'only_sell': this.onlySell,
        'only_buy': this.onlyBuy,
        'ignored_securities': this.ignoredSecurities,
        'constrain_us_stock_exposure': this.constrain_us_stock_exposure,
        'equity_focus': this.equityFocus,
        'overrides': this.overridesJSON,
        'symbol_proxies': this.symbolProxies,
        'custom_benchmark_id': this.customBenchmark?.entityId || null,
        'custom_benchmark_ticker_symbol': this.customBenchmark?.tickerSymbol || null,
      };

      const optimizerInputs = {
        ...baseConfig,
        clientRisk: this.clientRisk,
        selectedValueIds: this.selectedValueIds,
        specificInclusions: this.specificInclusions,
        specificExclusions: this.specificExclusions,
        specificDoNotBuy: this.specificDoNotBuy,
        specificDoNotSell: this.specificDoNotSell,
      };

      // If the config hasn't changed don't run the optimization again
      if (JSON.stringify(optimizerInputs) === JSON.stringify(this.lastOptimizerInputs)) return null;

      try {
        const data = await awaitTask(url, { method: 'POST', headers: { ...this.headers, 'Content-Type': 'application/json' }, body: JSON.stringify(baseConfig) });
        console.log('startOptimization, data: ', data);
        // Save optimizer config so we can check if it changes before running again
        this.lastOptimizerInputs = optimizerInputs;

        this.optimization = { optimizations: [...(this.optimization?.optimizations || []), data.task.meta] };
        console.log('NEW PORTFOLIO FROM OPTIMIZATION', data.task.meta.mp);

        return data.task.meta;
      } catch (error) {
        console.error('Optimization failed:', error);
        // Store the error for display
        this.optimizationError = error;
        throw error;
      }
    },
    selectOptimizedPortfolio(optimizationResult) {
      const optimizedPortfolio = {
        ...optimizationResult,
        label: 'optimizedPortfolio',
      };
      this.addPortfolio(optimizedPortfolio, 'optimizedPortfolio');
    },
    setAreas(newAreas) {
      this.areas = [...newAreas];
    },
    pushToAreas(newArea) {
      const index = this.areas.findIndex(area => area.name === newArea.name);
      if (index !== -1) {
      // Replace existing area
        this.areas.splice(index, 1, newArea);
      } else {
      // Add new area
        this.areas.push(newArea);
      }
    },
    updateArea(name, newArea) {
      const newAreas = this.areas.map(area => {
        if (area.name === name) {
          return { ...area, ...newArea };
        }
        return area;
      });
      this.setAreas(newAreas);
    },
    setPortfolio(newPortfolio) {
      this.portfolio = { ...newPortfolio, id: newPortfolio?.external_id || newPortfolio?.id };
    },
    setBenchmark(newBenchmark) {
      console.log('IN SET BENCHMARK', newBenchmark);
      if (newBenchmark?.id && newBenchmark?.id == this.benchmark?.id && this.benchmark?.equity_only_id && !newBenchmark?.equity_only_id) {
        newBenchmark.equity_only_id = this.benchmark.equity_only_id;
      }

      // Remove any existing benchmarks
      const withoutBenchmarks = this.portfolios.filter(portfolio => portfolio.label !== 'benchmark');

      // Add the new benchmark
      this.portfolios = [...withoutBenchmarks, { ...newBenchmark, label: 'benchmark', isChecked: true }];
    },
    setOptimizedPortfolio(newOptimizedPortfolio) {
      console.log('IN SET Optimized Portfolio', newOptimizedPortfolio);
      if (newOptimizedPortfolio?.id && newOptimizedPortfolio?.id == this.optimizedPortfolio?.id && this.optimizedPortfolio?.equity_only_id && !newOptimizedPortfolio?.equity_only_id) {
        newOptimizedPortfolio.equity_only_id = this.optimizedPortfolio.equity_only_id;
      }

      // Remove any existing optimizedPortfolios
      const withoutOptimizedPortfolio = this.portfolios.filter(portfolio => portfolio.label !== 'optimizedPortfolio');

      // Add the new benchmark
      this.portfolios = [...withoutOptimizedPortfolio, { ...newOptimizedPortfolio, label: 'optimizedPortfolio', isChecked: true }];
    },
    setCombinedPortfolio(newCombinedPortfolio) {
      const withoutCombinedPortfolio = this.portfolios.filter(portfolio => portfolio.label !== 'combinedPortfolio');
      this.portfolios = [...withoutCombinedPortfolio, { ...newCombinedPortfolio, label: 'combinedPortfolio' }];
    },
    // setCombinedBenchmark(newCombinedBenchmark) {
    //   const withoutCombinedPortfolio = this.portfolios.filter(portfolio => portfolio.label !== 'combinedBenchmark');
    //   this.portfolios = [...withoutCombinedPortfolio, { ...newCombinedBenchmark, label: 'combinedBenchmark' }];
    // },
    async getEntityLight(entity_external_id) {
      const url = `/api/v1/entities/${entity_external_id}/?light_serializer=true`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      return data;
    },
    async getEntityLightFromFund(fundTicker) {
      const url = `/api/v1/entities_from_fund/${fundTicker}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      return [data.portfolio, data.benchmark];
    },
    setAccountValue(accountValue) {
      this.accountValue = accountValue;
    },
    setAccounts(accounts) {
      // Annotate each account with its own useWholeShares flag based on custodian.
      // This avoids globally setting the store flag, which would bleed across accounts
      // (e.g. an Apex account shouldn't get whole-shares rounding just because a
      // co-loaded Schwab account does).
      this.accounts = accounts?.map(account => ({
        ...account,
        useWholeShares: !!(account?.custodian && !account.custodian.toLowerCase().includes('apex')),
      })) ?? [];
    },
    setUseWholeShares(useWholeShares) {
      this.useWholeShares = useWholeShares;
      this.useWholeSharesExplicit = useWholeShares;
    },
    updateChart(data, name) {
      this.charts = {
        ...this.charts,
        [name]: {
          ...this.charts[name],
          [data.id]: data.chart,
        },
      };
      this.saveUpdateTimestamp(name);
    },
    removeChart(id, name) {
      const areaCharts = { ...this.charts[name] };
      delete areaCharts[id];
      this.charts = { ...this.charts, [name]: areaCharts };
    },
    updateSlideImage(data, name) {
      this.slideImages = {
        ...this.slideImages,
        [name]: {
          ...this.slideImages[name],
          [data.id]: data.image,
        },
      };
      this.saveUpdateTimestamp(name);
    },
    removeSlideImage(data) {
      if (!data?.name) return;
      const areaImages = { ...this.slideImages[data.name] };
      delete areaImages[data.id];
      this.slideImages = { ...this.slideImages, [data.name]: areaImages };
    },
    saveUpdateTimestamp(name) {
      this.lastImageUpdates = {
        ...this.lastImageUpdates,
        [name]: Date.now(),
      };
    },
    toggleSlide(name, include) {
      const slides = { ...this.includeSlides };
      if (include) slides[name] = true;
      else delete slides[name];
      this.includeSlides = slides;
    },
    setLoadingState(key, state) {
      this.loadingStates = { ...this.loadingStates, [key]: state };
    },
    setManualClientRisk(risk) {
      this.manualClientRisk = risk;
    },
    // setshowClientRiskScore(show) {
    //   this.showClientRiskScore = show;
    // },
    async setSelectedValues(values) {
      console.log('CHECKUP STORE Person Entity SCREENSET', this.personEntity?.screen_sets);
      console.log('VALUES', values);
      const completeValues = await Promise.all(values.map(async value => {
        try {
          const response = await fetch(`/api/v1/metric_descriptions/${value.id}/`);
          const data = await response.json();
          console.log(`Value ID: ${value.id}`, data);
          return { id: value.id, metrics: data.map(item => ({ name: item.name }) ) }; // Data returns an array, extracting name from each item
        } catch (error) {
          console.error(`Error fetching data for value ID: ${value}`, error);
          return value.id; // Return the original value if there's an error
        }
      }));
      console.log('COMPLETE VALUES', completeValues);
      this.selectedValues = values;
      this.completeValues = completeValues;
    },
    setFeatureFlags(flags) {
      this.featureFlags = flags;
    },

    setMethodologyData(methodology, methodologyText) {
      let prev = this.methodologyData[methodology];
      if (!prev || typeof prev !== 'object') prev = {};
      const newMethodologyData = { ...prev, ...methodologyText };

      this.methodologyData = {
        ...this.methodologyData,
        [methodology]: newMethodologyData,
      };
    },
    getHoldingsFromClickData(clickData) {
      const chartTitle = clickData?.options?.plugins?.title?.text;
      if (this.portfolio?.name === chartTitle) {
        return this.portfolio.holdings.concat(this.portfolio.unmatched_holdings);
      }
      if (this.benchmark?.name === chartTitle) {
        return this.benchmark.holdings.concat(this.benchmark.unmatched_holdings);
      }
      if (this.optimizedPortfolio?.name === chartTitle) {
        return this.optimizedPortfolio.holdings.concat(this.optimizedPortfolio.unmatched_holdings);
      }
      throw new Error(`Could not find holdings for ${chartTitle}. Does this match one of the selected portfolios?`);
    },
    // TODO: Depreciate getHoldingsFromClickData and use getHoldingsFromName instead for getConcentrationLookthrough
    getHoldingsFromName(name, includeUnmatched = false) {
      let portfolio;
      if (this.portfolio?.name === name) portfolio = this.portfolio;
      if (this.benchmark?.name === name) portfolio = this.benchmark;
      if (this.optimizedPortfolio?.name === name) portfolio = this.optimizedPortfolio;

      if (portfolio) {
        const holdings = portfolio.holdings;
        const withUnmatched = holdings.concat(portfolio.unmatched_holdings || []);
        return includeUnmatched ? withUnmatched : holdings;
      }
      throw new Error(`Could not find holdings for ${name}. Does this match either the selected portfolio or benchmark name?`);
    },
    getConcentrationLookthrough(clickData) {
      const symbol = clickData?.label;
      if (!symbol) throw new Error('No symbol provided for holdings concentration clickthrough');

      const holdings = this.getHoldingsFromClickData(clickData);

      const lookthrough = [];
      if (!holdings?.length) throw new Error('No holdings provided for holdings concentration clickthrough');

      holdings.forEach(holding => {
        const securityBreakdown = holding.ticker.security_breakdown;
        if (!securityBreakdown) { // this is for unmatched holdings
          if (holding.ticker === symbol) { // if it's not the symbol we clicked on, don't show it
            lookthrough.push({ symbol: holding.ticker, full_name: holding.full_name, concentration: 1, dollars: holding.dollars, account: holding.account });
          }
          return;
        }
        const holdingsConcentration = securityBreakdown['Holdings Concentration'];
        const concentration = holdingsConcentration?.[symbol];
        if (!concentration) return;
        const dollars = holding.dollars * concentration;

        lookthrough.push({ symbol: holding.ticker.symbol, full_name: holding.ticker.full_name, concentration, dollars, account: holding.account });
      });
      // TODO dollars could sometimes be very small, maybe even less than a cent- figure out if we want to show those and/or how to do so
      // Sort it by concentration (descending)
      lookthrough.sort((a, b) => b.concentration - a.concentration);

      const formattedLookthrough = lookthrough.map(lookthroughEntry => {
        const formattedConcentration = `${(lookthroughEntry.concentration * 100).toFixed(2)}%`;
        const formattedDollars = lookthroughEntry.dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
        return { 'Symbol': lookthroughEntry.symbol, 'Name': lookthroughEntry.full_name, 'Concentration': formattedConcentration, 'Dollars Invested': formattedDollars, 'Account': lookthroughEntry.account };
      });

      return { table: formattedLookthrough };
    },
    getRiskLookthrough(clickData) {
      const field = 'Risk Score';
      const portfolioName = clickData?.label;
      if (!portfolioName) throw new Error(`No portfolio name provided for Trailing Returns clickthrough`);

      const holdings = this.getHoldingsFromName(portfolioName);
      if (!holdings?.length) throw new Error(`No holdings found for Trailing Returns clickthrough`);

      const table = [];
      holdings.forEach(holding => {
        const ticker = holding.ticker;
        if (!ticker || !ticker.symbol || !ticker.risk_score) return;
        const value = ticker.risk_score/100;
        if (!value && value !== 0) return;

        const dollars = holding.dollars?.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });
        const account = holding.account;

        table.push({
          'Symbol': ticker.symbol,
          'Name': ticker.full_name,
          'Dollars': dollars,
          [field]: (value * 100).toFixed(0),
          'Account': account,
          'sortBy': value,
        });
      });

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: portfolioName, table };
    },
    getTrailingReturnsLookthrough(clickData) {
      const function_map_dict = {
        '1 Year': 'trailing_1_yr_returns',
        '3 Year': 'trailing_3_yr_returns',
        '5 Year': 'trailing_5_yr_returns',
        '10 Year': 'trailing_10_yr_returns',
      };

      const field = clickData?.label + ' Returns';
      if (!field) throw new Error(`No field name provided for Trailing Returns clickthrough`);

      const portfolioName = clickData?.value?.label;
      if (!portfolioName) throw new Error(`No portfolio name provided for Trailing Returns clickthrough`);

      const holdings = this.getHoldingsFromName(portfolioName);
      if (!holdings?.length) throw new Error(`No holdings found for Trailing Returns clickthrough`);

      const table = [];

      holdings.forEach(holding => {
        const ticker = holding.ticker;
        if (!ticker || !ticker.symbol) return;

        const value = ticker.trailing_returns[function_map_dict[clickData.label]];
        if (!value && value !== 0) return;

        const dollars = holding.dollars?.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });

        const account = holding.account;

        table.push({
          'Symbol': ticker.symbol,
          'Name': ticker.full_name,
          'Dollars': dollars,
          [field]: (value * 100).toFixed(2) + '%',
          'Account': account,
          'sortBy': value,
        });
      });

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: portfolioName, table };
    },
    getTrailingVolatilitiesLookthrough(clickData) {
      const function_map_dict = {
        '1 Year': 'trailing_1_yr_volatility',
        '3 Year': 'trailing_3_yr_volatility',
        '5 Year': 'trailing_5_yr_volatility',
        '10 Year': 'trailing_10_yr_volatility',
      };

      const field = clickData?.label + ' Volatility';
      if (!field) throw new Error(`No field name provided for Trailing Volatilities clickthrough`);

      const portfolioName = clickData?.value?.label;
      if (!portfolioName) throw new Error(`No portfolio name provided for Trailing Volatilities clickthrough`);

      const holdings = this.getHoldingsFromName(portfolioName);
      if (!holdings?.length) throw new Error(`No holdings found for Trailing Volatilities clickthrough`);

      const table = [];

      holdings.forEach(holding => {
        const ticker = holding.ticker;

        if (!ticker || !ticker.symbol) return;

        const value = ticker.trailing_volatilities[function_map_dict[clickData.label]];

        if (!value && value !== 0) return;

        const dollars = holding.dollars?.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });
        const account = holding.account;

        table.push({
          'Symbol': ticker.symbol,
          'Name': ticker.full_name,
          'Dollars': dollars,
          [field]: (value * 100).toFixed(2) + '%',
          'Account': account,
          'sortBy': value,
        });
      });
      console.log('COMPLETED TABLE HERE');
      console.log(table);

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: portfolioName, table };
    },
    getExpenseRatioLookthrough(clickData) {
      const field = 'Expense Ratio';

      const portfolioName = clickData?.value?.label;
      if (!portfolioName) throw new Error(`No portfolio name provided for Expense Ratio clickthrough`);

      const holdings = this.getHoldingsFromName(portfolioName);
      if (!holdings?.length) throw new Error(`No holdings found for Expense Ratio clickthrough`);

      const table = [];

      holdings.forEach(holding => {
        const ticker = holding.ticker;

        if (!ticker || !ticker.symbol) return;

        const value = ticker.expense_ratio;

        if (value === undefined || value === null) return;

        const dollars = holding.dollars?.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });
        const account = holding.account;

        table.push({
          'Symbol': ticker.symbol,
          'Name': ticker.full_name,
          'Dollars': dollars,
          [field]: (value * 100).toFixed(2) + '%',
          'Account': account,
          'sortBy': value,
        });
      });
      console.log('COMPLETED TABLE HERE');
      console.log(table);

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: portfolioName, table };
    },
    getStatisticsLookthrough(clickData) {
      const field = clickData?.field;
      const portfolioName = clickData?.label;
      if (!portfolioName) throw new Error(`No portfolio name provided for Statistics clickthrough`);

      const holdings = this.getHoldingsFromName(portfolioName);
      if (!holdings?.length) throw new Error(`No holdings found for Statistics clickthrough`);

      const table = [];

      holdings.forEach(holding => {
        const ticker = holding.ticker;
        console.log('HOLDING TICKER');
        console.log(ticker);

        if (!ticker || !ticker.symbol) return;

        let value = null;
        let formattedvalue = null;
        if (field === 'Expense Ratio') {
          value = ticker.expense_ratio;
          if (!value && !['Mutual Fund', 'ETF'].includes(ticker.asset_type)) {
            value = 0;
          }
        } else if (field === 'Turnover') {
          value = ticker.portfolio_turnover;
          if (!value && !['Mutual Fund', 'ETF'].includes(ticker.asset_type)) {
            value = 0;
          }
        } else if (field === 'Yield') {
          value = ticker.security_breakdown['Current Yield']/100;
        } else if (field === 'Equity Dividend Yield') {
          value = ticker.security_breakdown['Equity Dividend Yield']/100;
        } else if (field === 'Fixed Income YTM') {
          value = ticker.security_breakdown['FI YTM']/100;
        } else if (field === 'Debt to Equity') {
          value = ticker.security_breakdown['Debt to Equity'];
        } else if (field === 'Forward PE Ratio') {
          value = ticker.security_breakdown['Forward PE'];
        } else if (field === 'Avg # Hldgs (funds)') {
          value = ticker.security_breakdown['# Holdings'];
        } else if (field === '3 Yr Mean Returns') {
          value = ticker.trailing_returns['trailing_3_yr_returns'];
        } else if (field === '3 Yr Standard Deviation') {
          value = ticker.trailing_volatilities['trailing_3_yr_volatility'];
        } else if (field === '5 Yr Mean Returns') {
          value = ticker.trailing_returns['trailing_5_yr_returns'];
        } else if (field === '5 Yr Standard Deviation') {
          value = ticker.trailing_volatilities['trailing_5_yr_volatility'];
        } else if (field === '10 Yr Mean Returns') {
          value = ticker.trailing_returns['trailing_10_yr_returns'];
        } else if (field === '10 Yr Standard Deviation') {
          value = ticker.trailing_volatilities['trailing_10_yr_volatility'];
        }


        if (value === undefined || value === null) return;

        if (['Avg # Hldgs (funds)'].includes(field)) {
          formattedvalue = value;
        } else if (['Debt to Equity', 'Forward PE Ratio'].includes(field)) {
          formattedvalue = (typeof value === 'number') ? value.toFixed(2) : '';
        } else {
          formattedvalue = (typeof value === 'number') ? (value * 100).toFixed(2) + '%' : 'N/A';
        }

        const dollars = holding.dollars?.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });
        const account = holding.account;

        table.push({
          'Symbol': ticker.symbol,
          'Name': ticker.full_name,
          'Dollars': dollars,
          [field]: formattedvalue,
          'Account': account,
          'sortBy': value,
        });
      });
      console.log('COMPLETED TABLE HERE');
      console.log(table);

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: portfolioName, table };
    },
    getSectorLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'Breakdown');
    },
    getAssetClassLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'AssetType');
    },
    getAssetLocationLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'LocationAssetType');
    },
    getStyleCategoryLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'StyleCategory');
    },
    getExtendedStyleLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'ExtendedStyle');
    },
    getMarketCapLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'Market Cap');
    },
    getGeographyLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'Geography');
    },
    getFixedIncomeTypeLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'FI Type');
    },
    getFixedIncomeMaturityLookthrough(clickData) {
      return this.getStandardLookthrough(clickData, 'Duration');
    },
    getAccountBreakdownLookthrough(clickData) {
      const account = clickData?.value.label;
      const portfolioName = this.portfolio?.name;
      if (!portfolioName) throw new Error(`No portfolio name provided for Account Breakdown clickthrough`);
      const holdings = this.getHoldingsFromName(portfolioName, true);
      const table = [];

      let filteredHoldings = [];
      if (account === portfolioName) {
        filteredHoldings = holdings;
      } else if (account) {
        filteredHoldings = holdings.filter(holding => holding.account === account);
      }

      if (filteredHoldings?.length) {
        const breakdownKey = 'AssetType';
        const field = clickData?.label;
        if (!field) throw new Error(`No field name provided for ${breakdownKey} clickthrough`);

        filteredHoldings.forEach(holding => {
          const ticker = holding.ticker;
          if (!ticker) return;

          const tickerSymbol = ticker?.symbol || holding.uploadedsymbol || 'UnknownSymbol';
          const tickerName = ticker?.full_name || holding.uploadedname || 'UnknownName';
          const tickerAccount = holding.account || '';
          let value = 0;
          const dollars = holding.dollars?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 2,
          });

          if (ticker.symbol) {
            const data = ticker.security_breakdown[breakdownKey];
            if (data) {
              value = data[field] || 0;
              if (field === 'Unclassified') {
                value += (data['Uncategorized'] || 0);
              }
            }
          } else if (breakdownKey === 'AssetType') {
            if ('securitytype' in holding) {
              if (field === 'Unclassified' && !['Stock', 'Bond', 'Cash'].includes(holding.securitytype)) {
                value += 1;
              } else if (field === 'Bonds' && holding.securitytype === 'Bond') {
                value += 1;
              }
            }
          }


          if (value) {
            table.push({
              'Symbol': tickerSymbol,
              'Name': tickerName,
              'Dollars': dollars,
              [field]: (value * 100).toFixed(2) + '%',
              'Account': tickerAccount,
              'sortBy': value,
            });
          }
        });
      }

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: account, table };
    },
    // Many of the chart clickthroughs follow a similar pattern and can probably use this getStandardLookthrough function
    // Note that some charts (like for holdings concentration) may need their own custom function
    getStandardLookthrough(clickData, breakdownKey) {
      if (!breakdownKey) throw new Error('No security breakdown key specified for chart clickthrough function');

      /* INPUT
      clickData = {
        label: 'Chart Item Label',
        value: {
          label: 'My Portfolio',
          ...
        }
      } */

      const field = clickData?.label;
      if (!field) throw new Error(`No field name provided for ${breakdownKey} clickthrough`);
      const portfolioName = clickData?.value?.label;
      if (!portfolioName) throw new Error(`No portfolio name provided for ${breakdownKey} clickthrough`);

      const taxability = clickData?.value?.taxability;

      let holdings;
      if (breakdownKey === 'AssetType' || breakdownKey === 'LocationAssetType') {
        holdings = this.getHoldingsFromName(portfolioName, true); // Correctly passing the argument here
      } else {
        holdings = this.getHoldingsFromName(portfolioName);
      }

      if (taxability) {
        holdings = holdings.filter(holding => holding.account_tax_status === taxability);
      }

      console.log('HOLDINGS ARE HERE IN FUNC');
      console.log(holdings);
      if (!holdings?.length) throw new Error(`No holdings found for ${breakdownKey} clickthrough`);

      const table = [];

      holdings.forEach(holding => {
        const ticker = holding.ticker;
        if (!ticker) return;

        const tickerSymbol = ticker?.symbol || holding.uploadedsymbol || 'UnknownSymbol';
        const tickerName = ticker?.full_name || holding.uploadedname || 'UnknownName';
        const tickerAccount = holding.account || '';
        let value = 0;
        const dollars = holding.dollars?.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        });

        if (ticker.symbol) {
          const data = ticker.security_breakdown[breakdownKey];
          if (data) {
            value = data[field] || 0;
            if (field === 'Unclassified') {
              value += (data['Uncategorized'] || 0);
            }
          }
        } else if (breakdownKey === 'AssetType') {
          if ('securitytype' in holding) {
            if (field === 'Unclassified' && !['Stock', 'Bond', 'Cash'].includes(holding.securitytype)) {
              value += 1;
            } else if (field === 'Bonds' && holding.securitytype === 'Bond') {
              value += 1;
            }
          }
        }


        if (value) {
          table.push({
            'Symbol': tickerSymbol,
            'Name': tickerName,
            'Dollars': dollars,
            [field]: (value * 100).toFixed(2) + '%',
            'Account': tickerAccount,
            'sortBy': value,
          });
        }
      });

      // Sort the data and then remove the sortBy field
      table.sort((a, b) => b.sortBy - a.sortBy);
      table.forEach(entry => delete entry.sortBy);

      return { title: portfolioName, table };
    },

    setActiveArea(area) {
      this.activeArea = area;
    },
    setStressTestResults(results) {
      this.stressTestResults = results;
    },
    setStressTestChartData(chartData) {
      this.stressTestChartData = chartData;
    },
  },
});
